import React, { useState, useEffect, useCallback } from 'react';
import i18next from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, AlertCircle, Euro, ChevronRight } from 'lucide-react';
const getLocaleCode = () => {
    const language = i18next.resolvedLanguage || i18next.language || "fr";
    if (language.startsWith("de")) return "de-DE";
    if (language.startsWith("en")) return "en-GB";
    return "fr-FR";
};
const formatCurrency = (value) => new Intl.NumberFormat(getLocaleCode(), {
    style: "currency", currency: "EUR", maximumFractionDigits: 0,
}).format(Number(value) || 0);
// Page de demande de validation après calcul
export default function EstimationValidationRequest() {
    useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { artworkId } = useParams();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [artwork, setArtwork] = useState(null);
    const [estimationData, setEstimationData] = useState(null);
    const [userPlan, setUserPlan] = useState('standard');
    const [validationCount, setValidationCount] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            // Charger l'œuvre
            const { data: artworkData } = await supabase
                .from('artworks')
                .select('*')
                .eq('id', artworkId)
                .single();
            if (artworkData)
                setArtwork(artworkData);
            // Charger le profil utilisateur
            const { data: profile } = await supabase
                .from('profiles')
                .select('plan')
                .eq('id', user.id)
                .single();
            if (profile)
                setUserPlan(profile.plan || 'standard');
            // Compter les validations déjà effectuées ce mois
            const startOfMonth = new Date();
            startOfMonth.setDate(1);
            startOfMonth.setHours(0, 0, 0, 0);
            const { count: validationsThisMonth } = await supabase
                .from('verification_requests')
                .select('*', { count: 'only', head: true })
                .eq('client_id', user.id)
                .gte('created_at', startOfMonth.toISOString());
            setValidationCount(validationsThisMonth || 0);
            // Récupérer les données d'estimation depuis location.state
            if (location.state?.estimationData) {
                setEstimationData(location.state.estimationData);
            }
            setLoading(false);
        }
        catch (error) {
            console.error('Erreur chargement:', error);
            setLoading(false);
        }
    }, [artworkId, location.state, user?.id]);
    useEffect(() => {
        if (user?.id && artworkId) {
            loadData();
        }
    }, [user?.id, artworkId, loadData]);
    const calculateCost = () => {
        if (userPlan === 'premium') {
            // Premium : gratuit jusqu'à 50 œuvres
            if (validationCount < 50)
                return 0;
            return 150; // Prix après quota
        }
        else {
            // Standard : gratuit jusqu'à 10 œuvres
            if (validationCount < 10)
                return 0;
            return 150; // Prix après quota
        }
    };
    const getRemainingFree = () => {
        if (userPlan === 'premium') {
            return Math.max(0, 50 - validationCount);
        }
        else {
            return Math.max(0, 10 - validationCount);
        }
    };
    const handleSubmitValidation = async () => {
        if (!estimationData) {
            alert(i18next.t("estimationValidation.request.ui.aucuneDonneeDEstimationTrouveeVeuillezDAbordEffectuer"));
            return;
        }
        setSubmitting(true);
        try {
            const cost = calculateCost();
            // Créer la demande de validation dans verification_requests
            const { error: insertError } = await supabase
                .from('verification_requests')
                .insert([{
                    client_id: user.id,
                    artwork_id: artworkId,
                    plan: userPlan,
                    type: estimationData.method === 'points' ? 'estimation_points' : 'estimation_comparables',
                    status: 'pending',
                    data: estimationData,
                    cost: cost,
                    created_at: new Date().toISOString()
                }]);
            if (insertError)
                throw insertError;
            alert(i18next.t("estimationValidation.request.ui.votreDemandeDeValidationAEteEnvoyeeAL"));
            navigate('/estimation');
        }
        catch (error) {
            console.error('Erreur envoi demande:', error);
            alert(i18next.t("estimationValidation.request.ui.submitError", { message: error.message }));
        }
        finally {
            setSubmitting(false);
        }
    };
    if (loading) {
        return (<div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600 font-light">{i18next.t("estimationValidation.request.ui.loading")}</p>
        </div>
      </div>);
    }
    if (!estimationData) {
        return (<div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-8">
            <div className="flex items-start gap-4">
              <AlertCircle size={32} className="text-yellow-600 flex-shrink-0"/>
              <div>
                <h2 className="text-xl font-semibold text-yellow-900 mb-3"><Trans i18nKey="estimationValidation.request.ui.calculDEstimationRequis"/> </h2>
                <p className="text-yellow-800 mb-4 font-light leading-relaxed"><Trans i18nKey="estimationValidation.request.ui.vousDevezDAbordEffectuerLeCalculDEstimation"/> </p>
                <button onClick={() => navigate('/estimation')} className="px-6 py-3 bg-yellow-600 text-white rounded-sm hover:bg-yellow-700 transition"><Trans i18nKey="estimationValidation.request.ui.retourAuxEstimations"/> </button>
              </div>
            </div>
          </div>
        </div>
      </div>);
    }
    const cost = calculateCost();
    const remainingFree = getRemainingFree();
    return (<div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <h1 className="text-4xl font-light tracking-tight mb-2"><Trans i18nKey="estimationValidation.request.ui.demandeDeValidationDEstimation"/> </h1>
          <p className="text-gray-600 font-light"><Trans i18nKey="estimationValidation.request.ui.serviceExpertArtkivValidationSous4872h"/> </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        
        {/* Récapitulatif de l'estimation */}
        <div className="bg-white rounded-sm border border-gray-200 p-8 mb-8">
          <h2 className="text-2xl font-light mb-6"><Trans i18nKey="estimationValidation.request.ui.recapitulatifDeVotreEstimation"/></h2>
          
          {artwork && (<div className="flex items-start gap-6 mb-6 pb-6 border-b">
              <div className="w-24 h-24 bg-gray-100 rounded-sm overflow-hidden flex-shrink-0">
                {artwork.images?.[0] ? (<img src={artwork.images[0].url || artwork.images[0].path} alt={artwork.title} className="w-full h-full object-cover"/>) : (<div className="w-full h-full flex items-center justify-center text-3xl">🖼️</div>)}
              </div>
              <div>
                <h3 className="text-xl font-medium mb-1">{artwork.title || i18next.t("estimationValidation.request.ui.sansTitre")}</h3>
                {artwork.artist && <p className="text-gray-600 font-light">{artwork.artist}</p>}
              </div>
            </div>)}

          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-700"><Trans i18nKey="estimationValidation.request.ui.methodeUtilisee"/></span>
              <span className="font-medium">
                {estimationData.method === 'points' ? i18next.t("estimationValidation.request.ui.methodeParPoints") : i18next.t("estimationValidation.request.ui.uvresComparables")}
              </span>
            </div>

            {/* Valeur médiane CUP (nouveau pipeline) */}
            {(estimationData.median_value || estimationData.estimatedValue) && (<div className="flex justify-between items-center">
                <span className="text-gray-700"><Trans i18nKey="estimationValidation.request.ui.valeurEstimee"/></span>
                <span className="text-2xl font-light">
                  {formatCurrency(estimationData.median_value || estimationData.estimatedValue)}
                </span>
              </div>)}

            {/* Fourchette CUP (nouveau pipeline) */}
            {(estimationData.price_low || estimationData.lowRange) && (estimationData.price_high || estimationData.highRange) && (<div className="flex justify-between items-center">
                <span className="text-gray-700">{i18next.t("estimationValidation.request.ui.range")}</span>
                <span className="text-sm text-gray-600">
                  {formatCurrency(estimationData.price_low || estimationData.lowRange)} – {formatCurrency(estimationData.price_high || estimationData.highRange)}
                </span>
              </div>)}

            {/* Confiance CUP */}
            {estimationData.cup_confidence && (<div className="flex justify-between items-center">
                <span className="text-gray-700"><Trans i18nKey="estimationValidation.request.ui.indiceDeConfiance"/></span>
                <span className="text-sm text-gray-500">{estimationData.cup_confidence}/100</span>
              </div>)}

            {/* Badge provisoire */}
            {estimationData.is_provisional && (<div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800"><Trans i18nKey="estimationValidation.request.ui.estimationProvisoireBaseDeComparablesInsuffisantePourValidationC"/> </div>)}
          </div>
        </div>

        {/* Tarification */}
        <div className={`rounded-sm border-2 p-8 mb-8 ${cost === 0 ? 'bg-green-50 border-green-500' : 'bg-blue-50 border-blue-500'}`}>
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${cost === 0 ? 'bg-green-500' : 'bg-blue-500'}`}>
              {cost === 0 ? (<CheckCircle size={24} className="text-white"/>) : (<Euro size={24} className="text-white"/>)}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-3">
                {cost === 0 ? i18next.t("estimationValidation.request.ui.validationGratuiteIncluse") : i18next.t("estimationValidation.request.ui.servicePayant")}
              </h3>
              
              <p className="text-sm mb-4 font-light leading-relaxed">
                {i18next.t("estimationValidation.request.ui.planIncludes", {
                    plan: userPlan === "premium" ? "Premium" : "Standard",
                    count: userPlan === "premium" ? 50 : 10,
                })}
              </p>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span><Trans i18nKey="estimationValidation.request.ui.validationsUtiliseesCeMois"/></span>
                  <span className="font-medium">{validationCount} / {userPlan === 'premium' ? '50' : '10'}</span>
                </div>
                
                {remainingFree > 0 ? (<div className="flex justify-between text-green-700">
                    <span><Trans i18nKey="estimationValidation.request.ui.validationsGratuitesRestantes"/></span>
                    <span className="font-semibold">{remainingFree}</span>
                  </div>) : (<div className="flex justify-between items-center">
                    <span><Trans i18nKey="estimationValidation.request.ui.coutDeCetteValidation"/></span>
                    <span className="text-2xl font-bold">{formatCurrency(cost)}</span>
                  </div>)}
              </div>

              {cost > 0 && (<div className="mt-4 pt-4 border-t border-blue-300">
                  <p className="text-xs text-gray-700">
                    💡 <strong>{i18next.t("estimationValidation.request.ui.tip")}</strong><Trans i18nKey="estimationValidation.request.ui.passezALAbonnementPremiumPourObtenir50Validations"/> </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Info processus */}
        <div className="bg-white rounded-sm border border-gray-200 p-8 mb-8">
          <h3 className="text-lg font-medium mb-4"><Trans i18nKey="estimationValidation.request.ui.commentCaMarche"/></h3>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-sm font-light">
                1
              </div>
              <div>
                <p className="font-medium mb-1"><Trans i18nKey="estimationValidation.request.ui.envoiDeVotreDemande"/></p>
                <p className="text-sm text-gray-600 font-light"><Trans i18nKey="estimationValidation.request.ui.votreCalculDEstimationEstTransmisALEquipe"/> </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-sm font-light">
                2
              </div>
              <div>
                <p className="font-medium mb-1"><Trans i18nKey="estimationValidation.request.ui.verificationExperte"/></p>
                <p className="text-sm text-gray-600 font-light"><Trans i18nKey="estimationValidation.request.ui.nosExpertsAnalysentVosDonneesVerifientLesSourcesEt"/> </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-sm font-light">
                3
              </div>
              <div>
                <p className="font-medium mb-1"><Trans i18nKey="estimationValidation.request.ui.receptionSous4872h"/></p>
                <p className="text-sm text-gray-600 font-light"><Trans i18nKey="estimationValidation.request.ui.vousRecevezLEstimationValideeParEmailAvecUn"/> </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex gap-4">
          <button onClick={handleSubmitValidation} disabled={submitting} className="flex-1 px-8 py-4 bg-black text-white rounded-sm hover:bg-gray-800 transition font-medium disabled:bg-gray-400 flex items-center justify-center gap-2">
            {submitting ? (i18next.t("estimationValidation.request.ui.envoiEnCours")) : (<><Trans i18nKey="estimationValidation.request.ui.envoyerPourValidation"/> {cost === 0 && <span className="text-xs opacity-75"><Trans i18nKey="estimationValidation.request.ui.gratuit"/></span>}
                {cost > 0 && <span className="text-xs opacity-75">({formatCurrency(cost)})</span>}
                <ChevronRight size={20}/>
              </>)}
          </button>
          
          <button onClick={() => navigate('/estimation')} className="px-8 py-4 border-2 border-gray-300 rounded-sm hover:border-black transition">
            Annuler
          </button>
        </div>

        {/* Info complémentaire */}
        <div className="mt-8 p-6 bg-gray-100 rounded-sm">
          <p className="text-xs text-gray-600 font-light leading-relaxed">
            <strong><Trans i18nKey="estimationValidation.request.ui.noteImportante"/></strong><Trans i18nKey="estimationValidation.request.ui.cetteValidationNEstPasUneExpertiseOfficielleCnes"/>{' '}
            <a href="/fiche/estimation" className="underline hover:text-black"><Trans i18nKey="estimationValidation.request.ui.enSavoirPlus"/></a>
          </p>
        </div>

      </main>
    </div>);
}
